import { useState, useRef } from "react";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { fileToDataUrl } from "@/lib/files";
import { roleLabel } from "@/lib/constants";
import { UserAvatar } from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { AppAndNotifications } from "@/components/AppAndNotifications";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const fileRef = useRef();
  const [name, setName] = useState(user.name);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || "");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [changingPw, setChangingPw] = useState(false);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Imagem demasiado grande (máx. 2MB)");
    const dataUrl = await fileToDataUrl(file);
    setAvatarUrl(dataUrl);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me/profile", { name, avatarUrl });
      updateUser(data);
      toast.success("Perfil atualizado");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) return toast.error("Preencha os campos");
    setChangingPw(true);
    try {
      await api.put("/users/me/password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Palavra-passe alterada");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader title="O Meu Perfil" subtitle="Personalize a sua conta e avatar." />
      <Card className="p-6 mb-6">
        <h3 className="font-poppins font-semibold mb-5">Dados pessoais</h3>
        <div className="flex items-center gap-5 mb-6">
          <UserAvatar user={{ name, avatarUrl }} size="h-20 w-20" />
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="avatar-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="avatar-upload">
              <Upload className="h-4 w-4 mr-2" /> Carregar foto
            </Button>
            {avatarUrl && (
              <Button variant="ghost" className="text-destructive" onClick={() => setAvatarUrl("")} data-testid="avatar-remove">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Nome completo</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="profile-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input value={user.email} disabled className="mt-1" />
            </div>
            <div>
              <Label>Função</Label>
              <Input value={roleLabel(user)} disabled className="mt-1" />
            </div>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="bg-primary text-black hover:bg-primary/90" data-testid="profile-save">
            {saving ? "A guardar..." : "Guardar alterações"}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="font-poppins font-semibold mb-5">Alterar palavra-passe</h3>
        <div className="space-y-4">
          <div>
            <Label>Palavra-passe atual</Label>
            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1" data-testid="pw-current" />
          </div>
          <div>
            <Label>Nova palavra-passe</Label>
            <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1" data-testid="pw-new" />
          </div>
          <Button onClick={changePassword} disabled={changingPw} variant="outline" data-testid="pw-save">
            {changingPw ? "A alterar..." : "Alterar palavra-passe"}
          </Button>
        </div>
      </Card>

      <AppAndNotifications />
    </div>
  );
}
